import { ChevronDown } from "lucide-react";
import { useMemo, useState } from "react";
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from "react-hook-form";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { IHotelsSearchSchema } from "../../types/hotel-search-type";

const childAgeOptions = Array.from({ length: 16 }, (_, index) => index + 2);

interface GuestCounterProps {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
}

interface ChildAgeSelectProps {
  roomIndex: number;
  childIndex: number;
  value: number | undefined;
  onChange: (value: number) => void;
  error?: { message?: string };
  form: UseFormReturn<IHotelsSearchSchema>;
}

interface HotelRoomAndGuestsProps {
  form: UseFormReturn<IHotelsSearchSchema>;
}

function GuestsCounter({ value, onChange, min, max }: GuestCounterProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 rounded-full"
        onClick={() => onChange(Math.max(min, value - 1))}
        disabled={value <= min}
      >
        -
      </Button>

      <span className="w-6 text-center text-sm">{value}</span>

      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-7 w-7 rounded-full"
        onClick={() => onChange(Math.min(max, value + 1))}
        disabled={value >= max}
      >
        +
      </Button>
    </div>
  );
}

function ChildAgeSelect({
  roomIndex,
  childIndex,
  value,
  onChange,
  error,
  form,
}: ChildAgeSelectProps) {
  return (
    <Field data-invalid={Boolean(error)}>
      <Select
        value={value ? String(value) : ""}
        onValueChange={(selectedValue) => {
          const nextAge = Number(selectedValue);
          const currentAges = [
            ...(form.getValues(`rooms.${roomIndex}.children_ages`) ?? []),
          ];
          currentAges[childIndex] = nextAge;

          onChange(nextAge);

          form.clearErrors(`rooms.${roomIndex}.children_ages.${childIndex}`);

          const requiredChildrenAges = currentAges.slice(
            0,
            form.getValues(`rooms.${roomIndex}.children`) ?? 0,
          );

          if (
            requiredChildrenAges.length ===
              (form.getValues(`rooms.${roomIndex}.children`) ?? 0) &&
            requiredChildrenAges.every(
              (age) => age !== undefined && age !== null,
            )
          ) {
            form.clearErrors(`rooms.${roomIndex}.children_ages`);
          }
        }}
      >
        <SelectTrigger
          id={`room-${roomIndex}-child-age-${childIndex}`}
          aria-invalid={Boolean(error)}
          className={cn("h-8 text-sm", Boolean(error) && "border-destructive")}
        >
          <SelectValue placeholder={`Child ${childIndex + 1}`} />
        </SelectTrigger>
        <SelectContent>
          {childAgeOptions.map((age) => (
            <SelectItem key={age} value={String(age)}>
              {age} years
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export default function HotelRoomAndGuests({ form }: HotelRoomAndGuestsProps) {
  const [open, setOpen] = useState(false);
  const {
    control,
    setValue,
    trigger,
    formState: { errors },
  } = form;

  const { fields, replace } = useFieldArray({ control, name: "rooms" });
  const watchRooms = useWatch({
    control,
    name: "rooms",
    defaultValue: [],
  });

  const totals = useMemo(() => {
    return (watchRooms ?? []).reduce(
      (acc, room) => ({
        adults: acc.adults + room.adults,
        children: acc.children + room.children,
        infants: acc.infants + room.infants,
      }),
      { adults: 0, children: 0, infants: 0 },
    );
  }, [watchRooms]);

  const totalRooms = watchRooms?.length ?? 0;
  const totalGuests = totals.adults + totals.children + totals.infants;

  async function validateBeforeClose() {
    const isValid = await trigger("rooms");

    if (isValid) {
      setOpen(false);
      return;
    }

    setOpen(true);
  }

  async function handleSave() {
    await validateBeforeClose();
  }

  function handleTotalRoomsChange(value: number) {
    const currentRooms = watchRooms ?? [];
    const currentLength = currentRooms.length;

    if (value > currentLength) {
      const newRooms = Array.from({ length: value - currentLength }, () => ({
        adults: 1,
        children: 0,
        infants: 0,
        children_ages: [] as number[],
      }));
      replace([...currentRooms, ...newRooms]);
      return;
    }

    replace(currentRooms.slice(0, value));
  }

  return (
    <Controller
      control={control}
      name="rooms"
      render={({ fieldState }) => (
        <Field data-invalid={fieldState.invalid || Boolean(errors.rooms)}>
          <Popover
            open={open}
            onOpenChange={(nextOpen) => {
              if (nextOpen) {
                setOpen(true);
                return;
              }

              void validateBeforeClose();
            }}
            modal
          >
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                aria-invalid={
                  fieldState.invalid || Boolean(errors.rooms?.length)
                }
                className={cn(
                  "h-14 w-full rounded-md border border-border bg-card px-4 text-left font-normal shadow-none hover:bg-muted/40 sm:h-[56px]",
                  (fieldState.invalid || Boolean(errors.rooms?.length)) &&
                    "border-destructive",
                )}
              >
                <span className="grid min-w-0 flex-1 grid-cols-[auto_1px_minmax(0,1fr)] items-center gap-3">
                  <span className="text-[1.5rem] font-bold leading-none tracking-tight text-foreground sm:text-[1.75rem]">
                    {totalGuests}
                  </span>
                  <span className="h-9 w-px bg-border" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-foreground/85">
                      {totalRooms} Room{totalRooms === 1 ? "" : "s"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {totals.adults} Adult{totals.adults === 1 ? "" : "s"}
                      {totals.children > 0
                        ? `, ${totals.children} ${totals.children === 1 ? "Child" : "Children"}`
                        : ""}
                      {totals.infants > 0
                        ? `, ${totals.infants} Infant${totals.infants === 1 ? "" : "s"}`
                        : ""}
                    </span>
                  </span>
                </span>
                <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>

            <PopoverContent
              className="p-2 gap-0"
              onInteractOutside={(event) => {
                event.preventDefault();
                void validateBeforeClose();
              }}
            >
              <div className="bg-muted  flex items-center justify-between rounded px-2 py-1.5">
                <p className="flex-1 text-sm">Total Rooms</p>
                <GuestsCounter
                  value={totalRooms}
                  onChange={handleTotalRoomsChange}
                  min={1}
                  max={10}
                />
              </div>

              {errors.rooms &&
              typeof errors.rooms === "object" &&
              !Array.isArray(errors.rooms) ? (
                <Alert variant="destructive" className="mb-2">
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{errors.rooms.message}</AlertDescription>
                </Alert>
              ) : null}

              <div className="max-h-[200px] space-y-4 overflow-y-auto pr-1">
                {fields.map((field, roomIndex) => (
                  <div key={field.id} className="space-y-3 rounded p-2 shadow">
                    <h3 className="border-muted-foreground pb-1 text-xs font-semibold border-b">
                      Room {roomIndex + 1}
                    </h3>

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Adults</p>
                        <p className="text-muted-foreground text-xs">
                          17+ years
                        </p>
                      </div>

                      <Controller
                        control={control}
                        name={`rooms.${roomIndex}.adults`}
                        render={({ field: adultsField }) => (
                          <GuestsCounter
                            value={adultsField.value}
                            onChange={adultsField.onChange}
                            min={1}
                            max={10}
                          />
                        )}
                      />
                    </div>

                    {errors.rooms?.[roomIndex]?.adults ? (
                      <span className="text-xs text-red-500">
                        {errors.rooms[roomIndex].adults?.message}
                      </span>
                    ) : null}

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Children</p>
                        <p className="text-muted-foreground text-xs">
                          2-17 years
                        </p>
                      </div>
                      <Controller
                        control={control}
                        name={`rooms.${roomIndex}.children`}
                        render={({ field: childrenField }) => (
                          <GuestsCounter
                            value={childrenField.value}
                            onChange={(value) => {
                              childrenField.onChange(value);

                              const currentAges =
                                watchRooms?.[roomIndex]?.children_ages ?? [];

                              if (value > currentAges.length) {
                                setValue(
                                  `rooms.${roomIndex}.children_ages`,
                                  [
                                    ...currentAges,
                                    ...Array(value - currentAges.length).fill(
                                      undefined,
                                    ),
                                  ],
                                  { shouldValidate: false },
                                );
                              } else {
                                setValue(
                                  `rooms.${roomIndex}.children_ages`,
                                  currentAges.slice(0, value),
                                  { shouldValidate: false },
                                );

                                for (
                                  let index = value;
                                  index < currentAges.length;
                                  index++
                                ) {
                                  form.clearErrors(
                                    `rooms.${roomIndex}.children_ages.${index}`,
                                  );
                                }
                              }
                            }}
                            min={0}
                            max={6}
                          />
                        )}
                      />
                    </div>

                    {(watchRooms?.[roomIndex]?.children ?? 0) > 0 ? (
                      <div className="grid grid-cols-2 gap-2">
                        {Array.from({
                          length: watchRooms?.[roomIndex]?.children ?? 0,
                        }).map((_, childIndex) => (
                          <Controller
                            key={childIndex}
                            control={control}
                            name={`rooms.${roomIndex}.children_ages.${childIndex}`}
                            render={({ field: childAgeField, fieldState }) => (
                              <ChildAgeSelect
                                roomIndex={roomIndex}
                                childIndex={childIndex}
                                value={childAgeField.value}
                                onChange={childAgeField.onChange}
                                error={fieldState.error}
                                form={form}
                              />
                            )}
                          />
                        ))}
                      </div>
                    ) : null}

                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm">Infants</p>
                        <p className="text-muted-foreground text-xs">
                          0-2 years
                        </p>
                      </div>

                      <Controller
                        control={control}
                        name={`rooms.${roomIndex}.infants`}
                        render={({ field: infantsField }) => (
                          <GuestsCounter
                            value={infantsField.value}
                            onChange={infantsField.onChange}
                            min={0}
                            max={4}
                          />
                        )}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Button
                type="button"
                className="mt-2 w-full"
                onClick={handleSave}
              >
                Apply
              </Button>
            </PopoverContent>
          </Popover>
        </Field>
      )}
    />
  );
}
